import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const ProductCategorizerViewResult = ({ result }) => {
  const getConfidenceColor = (score) => {
    if (!score) return 'bg-base-200 text-base-content';
    if (score >= 0.9) return 'bg-success/20 text-success';
    if (score >= 0.7) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const metadata = result?.metadata || {};
  const classification = result?.classification || {};
  const productAnalysis = result?.product_analysis || {};
  const confidenceMetrics = result?.confidence_metrics || {};
  const validationSummary = result?.validation_summary || {};
  const qualityIndicators = validationSummary?.quality_indicators || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Classification Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.FolderSimple size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Product Classification</h3>
              </div>

              {/* Primary Category */}
              {classification.primary_category && (
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Primary Category</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(classification.primary_category.confidence)}`}>
                      {Math.round((classification.primary_category.confidence || 0) * 100)}%
                    </span>
                  </div>
                  <div className="text-sm space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base-content/70">Name:</span>
                      <span>{classification.primary_category.name}</span>
                    </div>
                    {Array.isArray(classification.primary_category.path) && (
                      <div className="flex flex-wrap gap-1 items-center">
                        {classification.primary_category.path.map((item, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <PhosphorIcons.CaretRight size={12} className="text-base-content/50" />}
                            <span className="text-xs px-2 py-0.5 bg-base-300 rounded-full">{item}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subcategories */}
              {Array.isArray(classification.subcategories) && classification.subcategories.length > 0 && (
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Subcategories</span>
                  <div className="space-y-3">
                    {classification.subcategories.map((subcat, idx) => (
                      <div key={idx} className="p-2 bg-base-300 rounded">
                        <div className="flex justify-between items-center mb-1">
                          <span>{subcat.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(subcat.confidence)}`}>
                            {Math.round((subcat.confidence || 0) * 100)}%
                          </span>
                        </div>
                        {Array.isArray(subcat.path) && (
                          <div className="flex flex-wrap gap-1 items-center text-xs">
                            {subcat.path.map((item, pathIdx) => (
                              <React.Fragment key={pathIdx}>
                                {pathIdx > 0 && <PhosphorIcons.CaretRight size={12} className="text-base-content/50" />}
                                <span className="px-2 py-0.5 bg-base-200 rounded-full">{item}</span>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Product Analysis Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Analysis Details</h3>
              </div>

              {/* Key Features */}
              {Array.isArray(productAnalysis.key_features) && productAnalysis.key_features.length > 0 && (
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Key Features</span>
                  <div className="space-y-1">
                    {productAnalysis.key_features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <PhosphorIcons.Check size={16} className="text-success" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confidence Metrics */}
              <div className="p-3 bg-base-200 rounded-lg">
                <span className="font-medium block mb-2">Confidence Metrics</span>
                <div className="space-y-2 text-sm">
                  {Object.entries(confidenceMetrics).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-base-content/70 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(value)}`}>
                        {Math.round((value || 0) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Suggested Attributes */}
              {Array.isArray(classification.suggested_attributes) && classification.suggested_attributes.length > 0 && (
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Suggested Attributes</span>
                  <div className="flex flex-wrap gap-2">
                    {classification.suggested_attributes.map((attr, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs capitalize">
                        {attr.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="p-3 bg-base-200 rounded-lg">
                <span className="font-medium block mb-2">Metadata</span>
                <div className="space-y-2 text-sm">
                  {metadata.taxonomy_version && (
                    <div className="flex justify-between">
                      <span className="text-base-content/70">Taxonomy Version</span>
                      <span>{metadata.taxonomy_version}</span>
                    </div>
                  )}
                  {metadata.language && (
                    <div className="flex justify-between">
                      <span className="text-base-content/70">Language</span>
                      <span className="uppercase">{metadata.language}</span>
                    </div>
                  )}
                  {productAnalysis.market_segment && (
                    <div className="flex justify-between">
                      <span className="text-base-content/70">Market Segment</span>
                      <span className="capitalize">{productAnalysis.market_segment.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCategorizerViewResult;